import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { UploadCloud, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Drag-and-drop image uploader with click-to-browse and preview.
 *
 * Props:
 *  - value: current image URL (string). If starts with "/api/", we prefix with backend URL for preview.
 *  - onChange: (newUrl) => void
 *  - onUploadingChange: (bool) => void — optional; parent can disable submit while uploading
 *  - label: field label text
 *  - testid: base data-testid (e.g. "event-image")
 */
export default function ImageUploader({ value, onChange, onUploadingChange, label = "Image", testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const previewSrc = value ? (value.startsWith("/api/") ? `${BACKEND}${value}` : value) : "";

  const setUploadingState = (v) => {
    setUploading(v);
    onUploadingChange?.(v);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (jpg, png, gif, webp).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8MB.");
      return;
    }
    setUploadingState(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploadingState(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    uploadFile(file);
  };

  const clear = () => onChange("");

  return (
    <div className="block">
      <div className="text-xs text-[var(--ink-soft)] mb-1.5 font-mono-acc tracking-wider">{label}</div>

      {previewSrc ? (
        <div className="relative group border border-[var(--line)] rounded-sm overflow-hidden">
          <img
            src={previewSrc}
            alt="preview"
            className="w-full h-48 object-cover"
            data-testid={`${testid}-preview`}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs bg-white text-[var(--ink)] px-3 py-1.5 rounded-full hover:bg-[var(--bg-alt)]"
              data-testid={`${testid}-replace`}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={clear}
              className="text-xs bg-white text-red-600 px-3 py-1.5 rounded-full hover:bg-red-50"
              data-testid={`${testid}-remove`}
            >
              <X className="w-3 h-3 inline -mt-0.5" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`cursor-pointer border-2 border-dashed rounded-sm p-6 text-center transition ${
            dragOver ? "border-[var(--terracotta)] bg-[var(--terracotta)]/5" : "border-[var(--line)] hover:border-[var(--ink-soft)] bg-[var(--bg-alt)]/50"
          }`}
          data-testid={`${testid}-dropzone`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 text-sm text-[var(--ink-soft)]">
              <Loader2 className="w-6 h-6 animate-spin" />
              Uploading…
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-sm text-[var(--ink-soft)]">
              <UploadCloud className="w-8 h-8" />
              <div>
                <span className="text-[var(--ink)] font-medium">Drag &amp; drop</span> an image, or <span className="text-[var(--forest)] underline">click to browse</span>
              </div>
              <div className="text-[10px] font-mono-acc tracking-wider opacity-70">JPG · PNG · GIF · WEBP · max 8MB</div>
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => uploadFile(e.target.files?.[0])}
      />

      <div className="mt-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an image URL"
          className="w-full px-3 py-2 border border-[var(--line)] rounded-sm text-xs text-[var(--ink-soft)] focus:outline-none focus:border-[var(--terracotta)] bg-[var(--bg)]"
          data-testid={`${testid}-url`}
        />
      </div>
    </div>
  );
}
